/* What we count, and what we never send.
 *
 * Two projects, both the operator's and both off unless `GET /v1/config` names
 * them: PostHog in the EU for product counts, Sentry in the EU for errors.
 * Neither key is bundled. `lib/config.ts` holds the rule that silence means
 * off; this file holds what happens once something is switched on.
 *
 * **Nothing runs before the parent has answered the cookie box.** Not even the
 * cookieless mode: a tracker that starts itself and asks afterwards has
 * already done the thing it was asking about. Accept gives PostHog its cookie,
 * Reject gives it `persistence: "memory"`, which sets no cookie and writes no
 * storage key, and until one of the two is chosen PostHog is not loaded at
 * all.
 *
 * **Sentry does not wait on that choice, and it is not a cookie.** It stores
 * nothing on the device, sets no identifier, and exists so a screen that
 * breaks on a parent's phone is a thing we find out about. What it must never
 * carry is a child, so every event goes through the scrub below before it
 * leaves: request bodies dropped, learner ids and addresses dropped, and any
 * string over eighty characters dropped, which is the rule the service uses on
 * the same events.
 *
 * **What a PostHog event may carry.** An event name, and properties from a
 * short list of enums and counts. No child id, no learner id, no page hash, no
 * question, no answer, no file name, no address. The type is what enforces it:
 * `Props` has no string field a screen could put a name in.
 *
 * **Both libraries load lazily.** `import()` rather than a top-level import,
 * so a host with neither project configured never sends the tracking code down
 * a parent's connection at all, and the two chunks stay out of the first
 * paint on the hosts that do.
 */
import { useEffect, useSyncExternalStore } from "react";
import type { Config } from "./types";

/* --------------------------------------------------------------- the choice */

export type Choice = "accept" | "reject";

/** Where the answer to the cookie box is kept. One key, one word. */
const KEY = "mh.cookies";

const listeners = new Set<() => void>();

let choice: Choice | null | undefined;

function fromStorage(): Choice | null {
  try {
    const held = localStorage.getItem(KEY);
    return held === "accept" || held === "reject" ? held : null;
  } catch {
    // Safari private browsing throws on read. Nobody has answered, so nothing
    // runs and the box is shown again, which is the safe direction.
    return null;
  }
}

/** What the parent answered, or null where they have not been asked yet. */
export function heldChoice(): Choice | null {
  if (choice === undefined) choice = fromStorage();
  return choice;
}

function announce(): void {
  for (const fn of listeners) fn();
}

/** Write the answer down and act on it at once.
 *
 *  Acting on it is the half that is easy to leave out: a parent who accepted
 *  and then changed their mind from the privacy notice must not have to close
 *  the app for that to mean anything. Reject after accept wipes what PostHog
 *  wrote and moves the live instance to memory. */
export function choose(made: Choice, cfg: Config | null): void {
  choice = made;
  try {
    localStorage.setItem(KEY, made);
  } catch {
    /* The choice holds for this session and is asked again next launch. */
  }
  announce();
  if (made === "reject") wipePostHogStorage();
  if (cfg) void startPostHog(cfg, made);
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** The answer, as a screen reads it. Null means the box has not been answered
 *  and, where the host has a PostHog project, that the box is showing. */
export function useCookieChoice(): Choice | null {
  return useSyncExternalStore(subscribe, heldChoice, () => null);
}

/** Everything PostHog writes on a device, gone.
 *
 *  Exported because Reject has to be provable rather than asserted: the spec
 *  accepts, reloads, rejects, and then reads `document.cookie` and every
 *  storage key. Deleting a cookie is setting it to an instant in the past on
 *  the same path, which is the only way a browser offers. */
export function wipePostHogStorage(): void {
  try {
    for (const raw of document.cookie.split(";")) {
      const name = raw.split("=")[0]?.trim() ?? "";
      if (!name.startsWith("ph_")) continue;
      document.cookie =
        `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    }
  } catch {
    /* A document that will not talk about its cookies has none of ours. */
  }
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith("ph_")) localStorage.removeItem(key);
    }
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith("ph_")) sessionStorage.removeItem(key);
    }
  } catch {
    /* Private browsing. Nothing was written there either. */
  }
}

/* -------------------------------------------------------------- the events
 *
 * Five, and they are the five the operator asked for. The union is the whole
 * list: `track` takes no other name, so an event added in a hurry is a
 * compile error rather than a new column of unexamined data.
 */
export type ProductEvent =
  | "page sent"
  | "page read"
  | "typed question"
  | "credits opened"
  | "refusal shown"
  | "sample opened" | "sample completed" | "capture retake"
  | "practice opened" | "practice attempted" | "followup completed"
  | "report feedback";

/** Everything an event may carry.
 *
 *  There is no free string field here, and that is the design rather than an
 *  omission: the child's name, the question, the answer and the page hash are
 *  all strings, and a property bag that accepted one would eventually be
 *  handed one. `outcome` is the seven-state name the app already computes,
 *  `reason` is a refusal kind, `pages` and `status` are numbers.
 */
export interface Props {
  outcome?: string;
  reason?: "credits" | "cap" | "paused" | "quality" | "service";
  pages?: number;
  status?: number;
  /** Which screen, as a route pattern with no id in it. */
  screen?: string;
}

/* ------------------------------------------------------------------ PostHog */

/** The live instance, or null while there is none. Typed loosely on purpose:
 *  the module is loaded through `import()` and nothing here should pull its
 *  types into the first paint. */
interface Analytics {
  capture: (event: string, props?: Record<string, unknown>) => void;
  set_config: (config: Record<string, unknown>) => void;
  config: { persistence?: string };
}

let live: Analytics | null = null;
let loading: Promise<void> | null = null;

/** Events that happened before the library finished loading.
 *
 *  A launch fires a page view before an `import()` can resolve, and dropping
 *  it would lose the first screen of every session, which is the one screen a
 *  landing page is measured on. Capped, because a queue that grows is a leak
 *  and no screen is worth twenty events. */
const queued: { event: string; props: Record<string, unknown> }[] = [];
const QUEUE_MAX = 20;

/** Whether a value is safe to send.
 *
 *  Numbers, booleans and short enum-shaped strings only. Eighty characters is
 *  the service's own limit for the same job, and nothing this app sends
 *  legitimately comes near it. */
function safe(value: unknown): boolean {
  if (typeof value === "number" || typeof value === "boolean") return true;
  return typeof value === "string" && value.length > 0 && value.length <= 80;
}

/** The last gate before anything leaves. Runs on every property of every
 *  event, including the ones PostHog adds itself. */
export function sanitize(props: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    /* Two of PostHog's own properties carry the address of the page, and this
       app puts a page hash in a path. They are replaced by the route pattern
       the app sends itself, and dropped where it did not. */
    if (key === "$current_url" || key === "$pathname" || key === "$referrer"
      || key === "$initial_current_url" || key === "$initial_pathname") {
      continue;
    }
    if (safe(value)) out[key] = value;
  }
  return out;
}

/** Start PostHog, or move a running one to the persistence a new choice asks
 *  for. Does nothing at all where the host has no project. */
export async function startPostHog(cfg: Config, made: Choice | null):
    Promise<void> {
  if (!cfg.posthog || made === null) return;
  const persistence = made === "accept" ? "localStorage+cookie" : "memory";

  if (live) {
    live.set_config({ persistence });
    return;
  }
  if (loading) return loading;

  const { key, host } = cfg.posthog;
  loading = import("posthog-js").then(({ default: posthog }) => {
    /* **The choice is read again HERE, and not the one this call started
       with.** Codex, 5 September 2026: the dynamic import is a real gap, and a
       parent who pressed Accept and then Reject inside it would have had the
       library initialised with cookies from the first press, because the
       second press found `loading` set and returned. `heldChoice()` at the
       moment of `init` is the answer that stands, and a rejection reached
       during the import now initialises in memory. */
    const now = heldChoice();
    if (now === null) {
      loading = null;
      return;
    }
    posthog.init(key, {
      api_host: host,
      persistence: now === "accept" ? "localStorage+cookie" : "memory",
      /* Autocapture reads the text of whatever was clicked, and the text on
         these screens includes a child's first name. It is off, and it stays
         off. Session recording is off for the same reason, one size larger. */
      autocapture: false,
      disable_session_recording: true,
      capture_heatmaps: false,
      capture_dead_clicks: false,
      capture_exceptions: false,
      disable_surveys: true,
      /* The app sends its own page views with a route pattern, because a real
         path here carries a page hash. */
      capture_pageview: false,
      capture_pageleave: false,
      /* Nobody is identified. There is no person profile to build: the app
         sends no learner id, no account id and no address. */
      person_profiles: "never",
      /* No second host is contacted. Everything the library needs is in this
         bundle, and a script pulled from somewhere else is a request nobody
         reviewed. */
      disable_external_dependency_loading: true,
      advanced_disable_flags: true,
      cross_subdomain_cookie: false,
      secure_cookie: true,
      sanitize_properties: (props) => sanitize(props),
      loaded: (instance) => {
        live = instance as unknown as Analytics;
        /* On `window` deliberately: it is where the library puts itself when
           it is loaded from the snippet, it is how a support session reads the
           live configuration, and it is how the spec proves that Reject really
           did leave persistence in memory. */
        (window as unknown as Record<string, unknown>).posthog = instance;
        for (const held of queued.splice(0)) {
          instance.capture(held.event, held.props);
        }
      },
    });
  }).catch(() => {
    /* A chunk that will not load is not worth a word to anybody. The product
       works; the counting does not. */
    loading = null;
  });
  return loading;
}

function send(event: string, props: Record<string, unknown>): void {
  const clean = sanitize(props);
  if (live) {
    live.capture(event, clean);
    return;
  }
  if (loading && queued.length < QUEUE_MAX) queued.push({ event, props: clean });
}

/** Runtime allowlist: short names and raw identifiers are still private. */
export function productProps(props: Props): Record<string, unknown> {
  const clean: Record<string, unknown> = {};
  if (props.outcome && ['explained','no-cause','model-marked','corpus-marked','topic-only','no-topic','too-soft','not-read','reading'].includes(props.outcome)) clean.outcome = props.outcome;
  if (props.reason && ['credits','cap','paused','quality','service'].includes(props.reason)) clean.reason = props.reason;
  if (Number.isInteger(props.pages) && props.pages! >= 0 && props.pages! <= 100) clean.pages = props.pages;
  if (Number.isInteger(props.status) && props.status! >= 100 && props.status! <= 599) clean.status = props.status;
  if (props.screen) clean.screen = patternOf(props.screen);
  return clean;
}

/** One of the five. Never called with anything else: the union is the list. */
export function track(event: ProductEvent, props: Props = {}): void {
  send(event, productProps(props));
}

/* The routes this app has, as patterns. A whitelist rather than a rewrite,
 * because a rewrite is a guess about the shape of an address and a whitelist
 * cannot leak one: anything not on this list is counted as "/other" and the
 * address itself never leaves the phone.
 *
 * The three that matter are `/page/:sha`, `/practice/:sha` and `/reading/:job`.
 * A real path there carries the sha256 of a photograph of a child's work, and
 * a page view that sent it would put the identity of that page in an
 * analytics table for as long as the table exists.
 */
const STATIC_ROUTES = new Set([
  "/", "/how-it-works", "/pricing", "/privacy", "/signin", "/signin/code",
  "/consent", "/children/new", "/children/restore", "/home", "/capture",
  "/preview", "/type", "/pages", "/account", "/credits", "/admin", "/sample",
]);

const PATTERNS: [RegExp, string][] = [
  [/^\/page\/[^/]+\/confirm$/, "/page/:sha/confirm"],
  [/^\/page\/[^/]+$/, "/page/:sha"],
  [/^\/practice\/[^/]+$/, "/practice/:sha"],
  [/^\/reading\/[^/]+$/, "/reading/:job"],
  [/^\/paper\/[^/]+$/, "/paper/:paper"],
];

/** Which screen this is, as a pattern with no id in it. */
export function patternOf(pathname: string): string {
  const path = pathname.length > 1 && pathname.endsWith("/")
    ? pathname.slice(0, -1) : pathname;
  if (STATIC_ROUTES.has(path)) return path;
  for (const [rx, pattern] of PATTERNS) if (rx.test(path)) return pattern;
  return "/other";
}

/** A page view, as a route pattern rather than as an address.
 *
 *  `/page/:sha` is the one that matters: the real path carries the sha256 of a
 *  child's page, and a page view that sent it would put the identity of a
 *  photograph in an analytics table. The pattern goes instead, and
 *  `sanitize` drops PostHog's own copies of the address. */
export function trackPageView(pattern: string): void {
  send("$pageview", { screen: pattern });
}

/* ------------------------------------------------------------------- Sentry
 *
 * The scrub is written as two pure functions so a spec can assert on them
 * without a browser and without a live project. They are the same rule the
 * service applies to its own events.
 */

/** Eighty characters, the same limit the service uses. */
const LONG = 80;

/** An address, a learner id, a session token or a page hash. */
const IDENTIFIERS = [
  /[^\s@]+@[^\s@]+\.[^\s@]+/,           // an email address
  /\b[0-9a-f]{32,}\b/i,                  // a learner id, a sha256, a token
];

function tellsWho(value: string): boolean {
  return IDENTIFIERS.some((rx) => rx.test(value));
}

/** What a string becomes when it may not be sent. A marker rather than an
 *  empty string, so a report that lost something says so. */
const DROPPED = "[dropped]";

export function scrubText(value: string): string {
  if (value.length > LONG || tellsWho(value)) return DROPPED;
  return value;
}

/** Every value in a bag, scrubbed. Nested objects and arrays too: a breadcrumb
 *  carrying a request body is exactly the case this exists for. */
export function scrubValue(value: unknown, depth = 0): unknown {
  if (typeof value === "string") return scrubText(value);
  if (typeof value === "number" || typeof value === "boolean"
    || value === null || value === undefined) {
    return value;
  }
  if (depth >= 4) return DROPPED;
  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) => scrubValue(item, depth + 1));
  }
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      out[key] = scrubValue(item, depth + 1);
    }
    return out;
  }
  return DROPPED;
}

/** The shape of a Sentry event, as much of it as this file touches. Written
 *  here rather than imported so the scrub can be tested with no library. */
export interface ScrubbableEvent {
  message?: unknown;
  user?: unknown;
  request?: {
    data?: unknown; cookies?: unknown; headers?: unknown; url?: unknown;
    query_string?: unknown;
  };
  breadcrumbs?: unknown[];
  extra?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
  exception?: unknown;
  [key: string]: unknown;
}

/** What leaves for Sentry, or null where nothing may.
 *
 *  The request body goes whatever is in it, because what is in it is a
 *  photograph or a typed question. The user goes because there is nobody to
 *  name. Everything else is walked and scrubbed value by value. */
export function scrubEvent(event: ScrubbableEvent): ScrubbableEvent {
  const out: ScrubbableEvent = { ...event };
  delete out.user;
  if (out.request) {
    const { url } = out.request;
    out.request = {
      /* The address without its query and with no path: a path here carries a
         page hash and a query carries whatever was asked for. */
      url: typeof url === "string" ? originOf(url) : undefined,
    };
  }
  /* **The message goes whole, and so does every exception's `value`.** The
     eighty character rule keeps a sha or an address out, and a first name is
     four letters, so it passes: "Amy is not a number" is a message an app can
     write and it must not leave this device. The service's own scrub drops
     exception messages for the same reason, and this is that rule reaching the
     app, on the codex pass of 5 September 2026. What is left is the type and
     the stack, which is worse to debug and is the right side to be wrong on. */
  delete out.message;
  if (out.extra) out.extra = scrubValue(out.extra) as Record<string, unknown>;
  if (out.contexts) {
    out.contexts = scrubValue(out.contexts) as Record<string, unknown>;
  }
  if (Array.isArray(out.breadcrumbs)) {
    /* Through the same gate `beforeBreadcrumb` uses, because an event can
       arrive carrying breadcrumbs that were recorded before this was on. */
    out.breadcrumbs = out.breadcrumbs
      .map((crumb) => scrubCrumb(crumb as Record<string, unknown>))
      .filter((crumb) => crumb !== null);
  }
  if (out.exception) out.exception = dropExceptionValues(scrubValue(out.exception));
  return out;
}

/** Every `value` out of an exception chain, the type and stack kept.
 *
 *  Sentry puts the thrown message in `exception.values[].value` and a first
 *  name in a thrown message is short, ordinary text that every length and key
 *  rule lets through. Nothing is guessed about what a message holds: the
 *  field goes. */
function dropExceptionValues(exception: unknown): unknown {
  if (!exception || typeof exception !== "object") return exception;
  const held = exception as { values?: unknown };
  if (!Array.isArray(held.values)) return exception;
  return {
    ...held,
    values: held.values.map((one) => {
      if (!one || typeof one !== "object") return one;
      const { value: _dropped, ...rest } = one as Record<string, unknown>;
      return rest;
    }),
  };
}

/** The origin of an address, or nothing. */
function originOf(url: string): string | undefined {
  try {
    return new URL(url).origin;
  } catch {
    return undefined;
  }
}

/** An address with nothing identifying left in its path.
 *
 *  A breadcrumb for `GET /v1/reports/<sha>` carries the sha in its url, and the
 *  eighty character rule does not touch it because a sha is 64. So every
 *  segment that looks like an id becomes `:id`, the query goes whole, and a
 *  path of this app's own becomes the route pattern it already has a name
 *  for. */
export function safeUrl(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length === 0) return undefined;
  let url: URL;
  try {
    url = new URL(value, "https://mathshub.invalid");
  } catch {
    return undefined;
  }
  if (!url.pathname.startsWith("/v1/")) {
    return `${url.origin === "https://mathshub.invalid" ? "" : url.origin}`
      + patternOf(url.pathname);
  }
  const path = url.pathname.split("/")
    .map((segment) => (/^[0-9a-f]{8,}$/i.test(segment) || segment.length > 24
      ? ":id" : segment))
    .join("/");
  return url.origin === "https://mathshub.invalid" ? path : url.origin + path;
}

/** The only breadcrumb categories that may leave, and what may be in them.
 *
 *  **A whitelist, because the leak here is short strings rather than long
 *  ones.** A `ui.click` breadcrumb records the text of what was tapped, and
 *  the text on these screens is "Edit Sana" and "Reading Sana's page". A
 *  console breadcrumb records whatever anything logged. Neither is worth an
 *  error report, and no length rule would catch a first name, so both are
 *  dropped whole. What is left is a request line: where it went, how, and what
 *  came back. */
const CRUMB_KEEP = new Set(["fetch", "xhr", "navigation"]);

export function scrubCrumb(crumb: Record<string, unknown> | null):
    Record<string, unknown> | null {
  if (!crumb) return null;
  const category = typeof crumb.category === "string" ? crumb.category : "";
  if (!CRUMB_KEEP.has(category)) return null;
  const data = (crumb.data ?? {}) as Record<string, unknown>;
  const kept: Record<string, unknown> = {};
  const url = safeUrl(data.url);
  if (url) kept.url = url;
  const from = safeUrl(data.from);
  if (from) kept.from = from;
  const to = safeUrl(data.to);
  if (to) kept.to = to;
  if (typeof data.method === "string") kept.method = data.method;
  if (typeof data.status_code === "number") kept.status_code = data.status_code;
  return {
    type: crumb.type,
    category,
    level: crumb.level,
    timestamp: crumb.timestamp,
    data: kept,
  };
}

let sentryLoading: Promise<void> | null = null;

/** Start Sentry where the host has a project, and never otherwise. */
export async function startSentry(cfg: Config): Promise<void> {
  if (!cfg.sentry || sentryLoading) return sentryLoading ?? undefined;
  const { dsn } = cfg.sentry;
  sentryLoading = import("@sentry/react").then((Sentry) => {
    Sentry.init({
      dsn,
      /* No tracing and no replay. Both watch what a parent does on a screen
         that holds their child's work. */
      tracesSampleRate: 0,
      sendDefaultPii: false,
      /* The two gates, and everything goes through them. */
      beforeSend: (event) =>
        scrubEvent(event as unknown as ScrubbableEvent) as unknown as typeof event,
      beforeBreadcrumb: (crumb) =>
        scrubCrumb(crumb as unknown as Record<string, unknown>) as unknown as typeof crumb,
    });
  }).catch(() => {
    sentryLoading = null;
  });
  return sentryLoading;
}

/** Start what the host has switched on. Called once a launch, and again when
 *  the parent answers the cookie box. */
export function useTelemetry(cfg: Config | null): void {
  const made = useCookieChoice();
  useEffect(() => {
    if (!cfg) return;
    void startSentry(cfg);
    void startPostHog(cfg, made);
  }, [cfg, made]);
}

/** One page view per screen, and the credits screen counted as a product event
 *  on the way past.
 *
 *  "Credits screen opened" is the one of the five that is not a thing on the
 *  wire: nothing is requested that says a parent looked at what they have
 *  left. It is counted here, where the screen is entered, and it is the same
 *  fact the page view carries under a name the operator asked for. */
export function usePageViews(pathname: string): void {
  /* The choice is a dependency, not decoration. Nothing is counted before the
     parent answers the box, so the screen they were standing on when they
     answered would otherwise be the one screen never counted, and on a launch
     that is the landing page. */
  const made = useCookieChoice();
  useEffect(() => {
    if (made === null) return;
    const pattern = patternOf(pathname);
    trackPageView(pattern);
    if (pattern === "/credits") track("credits opened", { screen: pattern });
  }, [pathname, made]);
}
